import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Chip,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Switch
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { PageTitle } from '../components/atoms/PageTitle'
import { PageSubtitle } from '../components/atoms/PageSubtitle'
import { Button } from '../components/atoms/Button'
import { callAxios } from '../hooks/useAxios'
import { useApp } from '../hooks/useApp'

interface NotificationItem {
  userId: number
  meterNumber: string
  kind: string
  upKw: number | null
  downKw: number | null
  occurredAt: string
}

interface PendingMessage {
  brpCode: string
  brpName: string | null
  email: string
  subject: string
  body: string
  items: NotificationItem[]
}

interface SentNotification {
  id: number
  brpCode: string
  brpName: string | null
  email: string
  subject: string
  body: string
  itemCount: number
  sentAt: string
}

interface NotificationState {
  autoSend: boolean
  pendingCount: number
  warnings: string[]
  messages: PendingMessage[]
  sent: SentNotification[]
}

const bodySx = {
  fontFamily: 'monospace',
  fontSize: '0.85em',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  border: '1px solid rgba(128,128,128,0.35)',
  borderRadius: 1,
  p: 1.5
}

export function AdminBrpNotificationsPage(): React.ReactElement {

  const { t } = useTranslation()
  const app = useApp()
  const [state, setState] = useState<NotificationState | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const handle = (promise: ReturnType<typeof callAxios<NotificationState>>, info?: string) => {
    setBusy(true)
    promise.then(res => {
      setBusy(false)
      if (res.success) {
        setState(res.data)
        if (info) { app.addInfo(info) }
      } else {
        app.addError(res)
      }
    })
  }

  useEffect(() => {
    handle(callAxios<NotificationState>({ url: '/admin/brp-notifications' }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onRefresh = () => {
    handle(callAxios<NotificationState>({ method: 'POST', url: '/admin/brp-notifications/refresh' }),
      t('Bookkeeping refreshed.'))
  }

  const onSend = () => {
    setConfirmOpen(false)
    handle(callAxios<NotificationState>({ method: 'POST', url: '/admin/brp-notifications/send' }),
      t('Notifications sent.'))
  }

  const onToggleAutoSend = (enabled: boolean) => {
    handle(callAxios<NotificationState>({ method: 'PUT', url: '/admin/brp-notifications/auto-send' }, { enabled }),
      enabled ? t('Automatic sending enabled.') : t('Automatic sending disabled.'))
  }

  return (
    <Box>
      <PageTitle title={t('BRP notifications')}/>
      <Box sx={{ mb: 2 }}>
        <Trans>Reserve-use notifications to balance responsible parties (FCR terms, appendix 1 ch. 4). The bookkeeping refreshes nightly; messages batch all changes per BRP and are throttled to one email per BRP per 7 days.</Trans>
      </Box>

      <FormControlLabel
        control={
          <Switch
            data-cy='switch-BRP auto-send'
            checked={state?.autoSend || false}
            disabled={busy || !state}
            onChange={e => onToggleAutoSend(e.target.checked)}
          />}
        label={t('Send automatically (daily, after the nightly refresh)')}
      />
      {!state?.autoSend &&
        <Box sx={{ opacity: 0.7, fontSize: '0.9em', mb: 1 }}>
          <Trans>While off, the nightly run only refreshes the bookkeeping — nothing is emailed without the Send button below.</Trans>
        </Box>}

      <Stack direction="row" spacing={2} sx={{ my: 2 }}>
        <Button data-cy='button-Refresh bookkeeping' onClick={onRefresh} disabled={busy}>
          <Trans>Refresh bookkeeping</Trans>
        </Button>
        <Button
          data-cy='button-Send now'
          onClick={() => setConfirmOpen(true)}
          disabled={busy || !state || state.messages.length === 0}
        >
          <Trans>Send now</Trans>{state ? ` (${state.messages.length})` : ''}
        </Button>
      </Stack>

      <PageSubtitle sx={{ mt: 3 }} title={`${t('Outgoing messages')} (${state?.messages.length ?? '…'})`}/>
      {state && state.messages.length === 0 &&
        <Box sx={{ opacity: 0.5, mb: 2 }}><Trans>Nothing to send right now.</Trans></Box>}
      {state?.messages.map(message =>
        <Accordion key={message.brpCode} data-cy={`pending-${message.brpCode}`}>
          <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Box sx={{ fontWeight: 600 }}>{message.brpName || message.brpCode}</Box>
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{message.email}</Box>
              <Chip size="small" label={`${message.items.length} ${t('changes')}`}/>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 1, opacity: 0.7 }}>{message.subject}</Box>
            <Box sx={bodySx}>{message.body}</Box>
          </AccordionDetails>
        </Accordion>
      )}

      {state && state.warnings.length > 0 && <>
        <PageSubtitle sx={{ mt: 3 }} title={`${t('Warnings')} (${state.warnings.length})`}/>
        {state.warnings.map((warning, i) =>
          <Alert key={i} severity="warning" sx={{ mb: 0.5 }}>{warning}</Alert>)}
      </>}

      <PageSubtitle sx={{ mt: 3 }} title={t('Sent notifications')}/>
      {state && state.sent.length === 0 &&
        <Box sx={{ opacity: 0.5 }}><Trans>Nothing sent yet.</Trans></Box>}
      {state?.sent.map(notification =>
        <Accordion key={notification.id}>
          <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Box sx={{ opacity: 0.7 }}>{new Date(notification.sentAt).toLocaleString()}</Box>
              <Box sx={{ fontWeight: 600 }}>{notification.brpName || notification.brpCode}</Box>
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{notification.email}</Box>
              <Chip size="small" label={`${notification.itemCount} ${t('changes')}`}/>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={bodySx}>{notification.body}</Box>
          </AccordionDetails>
        </Accordion>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle><Trans>Send BRP notifications?</Trans></DialogTitle>
        <DialogContent>
          <Trans>This emails every message listed under outgoing messages to the real BRP addresses.</Trans>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}><Trans>Cancel</Trans></Button>
          <Button data-cy='button-Confirm send' onClick={onSend}><Trans>Send</Trans></Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
